<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router/auto'
import { sendVerificationCode, verifyCode } from '@/api/auth'
import { ApiError } from '@/api/client'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

definePage({
  meta: { layout: 'blank', public: true, publicOnly: true },
})

const router = useRouter()
const route = useRoute()

/**
 * Avanza de paso y **avisa si la navegación no ocurrió**.
 *
 * `router.push` no lanza: devuelve un `NavigationFailure` que, ignorado, deja
 * un botón que responde y no pasa nada — sin rastro en consola ni en pantalla.
 * Nos costó una sesión entera de diagnóstico a ciegas.
 */
const go = async (to: RouteLocationRaw) => {
  const failure = await router.push(to)

  if (failure)
    console.error('[register] navigation blocked:', failure)

  return failure
}

/*
 * El email entra por DOS caminos, y el orden importa.
 *
 * 1. `?email=` — el link directo del mail de invitación, que es la vía normal
 *    ahora que el alta pública está cerrada. Quien lo abre no pasó por ningún
 *    paso previo, así que no hay nada en `sessionStorage`.
 * 2. `sessionStorage` — el alta self-service, cuando el flag la reabre.
 *
 * Sin el primero, el link del mail rebotaba hasta `/register` y el proveedor
 * terminaba con el código en la mano y sin dónde escribirlo.
 */
const queryEmail = typeof route.query.email === 'string' ? route.query.email.trim() : ''
const email = ref(queryEmail || sessionStorage.getItem('bt_register_email') || '')

// Se persiste para que el paso final lo encuentre donde siempre.
if (queryEmail)
  sessionStorage.setItem('bt_register_email', queryEmail)

// VOtpInput ya resuelve avance de foco, pegado y backspace.
const code = ref('')
const loading = ref(false)
const error = ref('')
const resendCountdown = ref(60)
const resending = ref(false)

let intervalId: number | undefined

const startCountdown = () => {
  resendCountdown.value = 60
  if (intervalId)
    clearInterval(intervalId)

  intervalId = window.setInterval(() => {
    resendCountdown.value--
    if (resendCountdown.value <= 0 && intervalId) {
      clearInterval(intervalId)
      intervalId = undefined
    }
  }, 1000)
}

onMounted(() => {
  if (!email.value) {
    // A login y no a `register-email`: con el alta cerrada ese paso rebota, y
    // encadenar dos redirecciones para terminar en el mismo lugar solo hace
    // parpadear la pantalla.
    router.replace({ name: 'login' })

    return
  }
  startCountdown()
})

onBeforeUnmount(() => {
  if (intervalId)
    clearInterval(intervalId)
})

const submit = async () => {
  if (loading.value)
    return

  error.value = ''
  if (code.value.length !== 4) {
    error.value = 'Enter the 4-digit code.'

    return
  }

  loading.value = true
  try {
    // `verifyOnly` deja el código vivo: se vuelve a mandar en el registro final.
    const res = await verifyCode(email.value, code.value, true)

    sessionStorage.setItem('bt_register_code', code.value)

    /*
     * El tipo de cuenta lo fijó el admin al invitar. Es el único lugar del que
     * el paso final puede sacarlo: el paso donde el proveedor lo elegía es
     * parte del alta pública y con ella cerrada no se recorre.
     */
    if (res.accountType)
      sessionStorage.setItem('bt_register_account_type', res.accountType)
    await go({ name: 'register-setup' })
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Verification failed.'
  }
  finally {
    loading.value = false
  }
}

const resend = async () => {
  if (resendCountdown.value > 0 || resending.value)
    return

  resending.value = true
  error.value = ''
  try {
    await sendVerificationCode(email.value)
    startCountdown()
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Could not resend code.'
  }
  finally {
    resending.value = false
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-md-none d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    class="auth-wrapper"
    no-gutters
  >
    <AuthIllustration />

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
      style="background-color: rgb(var(--v-theme-surface));"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            Check your email 📧
          </h4>
          <p class="mb-0">
            We sent a 4-digit code to <strong>{{ email }}</strong>.
          </p>
        </VCardText>

        <VCardText>
          <VAlert
            v-if="error"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            {{ error }}
          </VAlert>

          <!-- @finish dispara al completar los 4 dígitos → verificación automática. -->
          <VOtpInput
            v-model="code"
            :length="4"
            type="number"
            :disabled="loading"
            class="mb-4 pa-0"
            @finish="submit"
          />

          <VBtn
            block
            :loading="loading"
            :disabled="code.length !== 4"
            @click="submit"
          >
            Verify
          </VBtn>

          <div class="text-center text-body-2 mt-4">
            <template v-if="resendCountdown > 0">
              Resend code in {{ resendCountdown }}s
            </template>
            <VBtn
              v-else
              variant="text"
              size="small"
              :loading="resending"
              @click="resend"
            >
              Resend code
            </VBtn>
          </div>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
